/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (R8, R9)
 *
 * One balances query per address set, read by selectors.
 *
 * - The user's own wallets share one query, `['balances', sortedOwnAddrs]`, polled every 60 s
 *   (never while the tab is hidden) by its single owner, BalancesProvider.
 * - Any other address (AddressPage for someone else's wallet) gets `['balances', [address]]`,
 *   polled by the page that shows it.
 * - Every per-asset hook is a selector over those queries: mounting one never adds a request,
 *   because selectors share the query and do not poll.
 *
 * Selectors return a value only while it is available (≤ 5 min old) and re-render when it
 * expires, so a form reading them can never act on a captured number.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useSyncExternalStore,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ACCELERATED_RETRY_MS,
  floorFor,
  floorsVersion,
  isAccelerating,
  isBypassPending,
  isDeliveredFresh,
  minBlockFor,
  readHonouringFloors,
  settleFloors,
  subscribeFloors,
} from '@/lib/balance-floors';
import { readObservation } from '@/lib/balance-freshness';
import { BALANCES_QUERY_ROOT } from '@/lib/balance-refresh';
import {
  chainOfAsset,
  fetchBalances,
  type BalanceAsset,
  type BalancesResponse,
  type Observation,
  type ReadChain,
} from '@/lib/read-api';
import { ASSET_METADATA } from '@/lib/token-metadata';

/** The server accepts at most 8 addresses per request; the drawer shows at most 8 wallets. */
export const MAX_BALANCE_ADDRESSES = 8;
export const BALANCES_POLL_MS = 60_000;

export interface TokenBalanceData {
  /** Raw balance in base units. */
  balance: bigint;
  symbol: string;
  decimals: number;
  /** Last-known value: show it marked stale, but never let it enable an amount-dependent action. */
  stale: boolean;
}

export interface BalancesSnapshot {
  readonly response: BalancesResponse;
  /** Client time the response arrived (client clock only). */
  readonly receivedAt: number;
}

const NO_ADDRESSES: readonly string[] = [];

/** Sorted, lower-cased, de-duplicated query key for an address set. */
export function balancesKeyFor(addresses: readonly string[]): readonly string[] {
  const unique = [...new Set(addresses.map((address) => address.toLowerCase()))];
  return unique.slice(0, MAX_BALANCE_ADDRESSES).sort();
}

/** The user's own wallets as a balances key; provided by BalancesProvider. */
export const OwnBalancesContext = createContext<readonly string[]>(NO_ADDRESSES);

export function balancesQueryOptions(addresses: readonly string[]) {
  return {
    queryKey: [BALANCES_QUERY_ROOT, addresses] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<BalancesSnapshot> => {
      // Every fetch carries the unsatisfied floors, ordinary polls included.
      const minBlock = minBlockFor(addresses);
      const response = await readHonouringFloors<BalancesResponse>(
        {
          bypass: ['base', 'polygon'],
          addresses,
          floorScopes: Object.keys(minBlock) as ReadChain[],
          delivered: (result, { scope, address, item }) => {
            const balances = result.byAddress[address.toLowerCase()];
            const section: Readonly<Record<string, Observation>> | undefined =
              scope === 'polygon' ? balances?.polygon : balances?.base;
            return isDeliveredFresh(section?.[item]);
          },
        },
        ({ withFloors, bypass }) =>
          fetchBalances(
            addresses,
            {
              minBlock: withFloors ? minBlock : undefined,
              bypassCache: { base: bypass.has('base'), polygon: bypass.has('polygon') },
            },
            signal
          )
      );
      settleFloors(response);
      return { response, receivedAt: Date.now() };
    },
    enabled: addresses.length > 0,
    // Selectors never trigger a fetch of their own; the owner's poll keeps the data current.
    staleTime: BALANCES_POLL_MS,
  };
}

function pollInterval(addresses: readonly string[]): number {
  const now = Date.now();
  return isAccelerating('base', addresses, now) || isAccelerating('polygon', addresses, now)
    ? ACCELERATED_RETRY_MS
    : BALANCES_POLL_MS;
}

/** The one poller for an address set. Renders nothing itself (it subscribes to no props). */
export function useBalancesPoll(addresses: readonly string[]): void {
  useQuery({
    ...balancesQueryOptions(addresses),
    refetchInterval: () => pollInterval(addresses),
    refetchIntervalInBackground: false,
    notifyOnChangeProps: [],
  });
}

/** Which balances query an address belongs to: the user's own set, or its own one. */
function useBalancesKey(address: string | null): readonly string[] {
  const own = useContext(OwnBalancesContext);
  const lower = address?.toLowerCase() ?? null;
  return useMemo(() => {
    if (lower === null) return NO_ADDRESSES;
    return own.includes(lower) ? own : [lower];
  }, [lower, own]);
}

/**
 * Poll the balances of an address the page shows when it is NOT one of the user's wallets (their
 * own query is already polled by BalancesProvider), so a foreign address gets exactly one poller.
 */
export function useAddressBalancesPoll(address: string | null): void {
  const own = useContext(OwnBalancesContext);
  const key = useBalancesKey(address);
  useBalancesPoll(key === own ? NO_ADDRESSES : key);
}

/** Re-render once at `at` (client time), e.g. when a shown value expires. */
export function useRerenderAt(at: number | null): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (at === null) return;
    const id = setTimeout(bump, Math.max(0, at - Date.now()));
    return () => clearTimeout(id);
  }, [at]);
}

export interface SelectedBalance {
  readonly data: TokenBalanceData | undefined;
  readonly expiresAt: number | null;
}

/** Pure selection over a snapshot; `undefined` data means unavailable ("—"). */
export function selectBalance(
  snapshot: BalancesSnapshot | undefined,
  address: string,
  asset: BalanceAsset,
  refreshFailed: boolean,
  now: number = Date.now()
): SelectedBalance {
  const chain = chainOfAsset(asset);
  const balances = snapshot?.response.byAddress[address.toLowerCase()];
  const observation =
    chain === 'base'
      ? balances?.base[asset as keyof typeof balances.base]
      : balances?.polygon[asset as keyof typeof balances.polygon];
  const reading = snapshot
    ? readObservation(observation, {
        receivedAt: snapshot.receivedAt,
        now,
        floor: floorFor(chain, address),
        refreshFailed,
        refreshPending: isBypassPending(chain, address, asset),
      })
    : null;
  if (!reading) return { data: undefined, expiresAt: null };
  const { symbol, decimals } = ASSET_METADATA[asset];
  return {
    data: { balance: reading.value, symbol, decimals, stale: reading.stale },
    expiresAt: reading.expiresAt,
  };
}

export interface BalanceSelectorResult {
  /** undefined while loading or when the value is unavailable. */
  data: TokenBalanceData | undefined;
  isLoading: boolean;
}

/** Selector for one asset of one address. */
export function useBalance(address: string | null, asset: BalanceAsset): BalanceSelectorResult {
  const addresses = useBalancesKey(address);
  const query = useQuery(balancesQueryOptions(addresses));
  useSyncExternalStore(subscribeFloors, floorsVersion);
  const selected = address
    ? selectBalance(query.data, address, asset, query.isRefetchError)
    : { data: undefined, expiresAt: null };
  useRerenderAt(selected.expiresAt);
  return { data: selected.data, isLoading: query.isLoading };
}

/** Selector for several addresses at once (the drawer); one subscription for all of them. */
export function useBalancesForWallets(addresses: readonly string[]): {
  select: (address: string, asset: BalanceAsset) => TokenBalanceData | undefined;
  isLoading: boolean;
} {
  const key = useMemo(() => balancesKeyFor(addresses), [addresses]);
  const query = useQuery(balancesQueryOptions(key));
  useSyncExternalStore(subscribeFloors, floorsVersion);
  const now = Date.now();
  let earliest: number | null = null;
  const select = (address: string, asset: BalanceAsset): TokenBalanceData | undefined => {
    const selected = selectBalance(query.data, address, asset, query.isRefetchError, now);
    if (selected.expiresAt !== null && (earliest === null || selected.expiresAt < earliest)) {
      earliest = selected.expiresAt;
    }
    return selected.data;
  };
  // Evaluate every value once so the next expiry is known before scheduling the re-render.
  for (const address of key) {
    for (const asset of Object.keys(ASSET_METADATA) as BalanceAsset[]) select(address, asset);
  }
  useRerenderAt(earliest);
  return { select, isLoading: query.isLoading };
}

/** A balance that may back an amount-dependent action: available and not stale. */
export function freshBalance(data: TokenBalanceData | undefined): bigint | null {
  return data && !data.stale ? data.balance : null;
}
